import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { datiPeriodoDaJson, regolePeriodoDaJson, type ModelloDichiarazione } from '@praticheenergia/ad1-engine'
import { etichettaStatoAdm, type Periodo } from '@praticheenergia/domain'
import { formattaEuro, formattaKwh, riepilogoModello } from './riepilogo'
import {
  caricaDichiarazione,
  confermaDichiarazione,
  caricaFirmato,
  rettificaDichiarazione,
  type InvioPortale,
  type StatoDichiarazione,
} from './dichiarazione.api'
import { useCalcolo } from './useCalcolo'
import { EditorElenchi } from './EditorElenchi'
import type { Elenchi } from './elenchi.api'
import { scarica, scaricaDaInvio, avviaDownload, FORMATI_SCARICABILI, type FormatoInvio } from './download.api'
import { spiegaErroreAdm } from './errori-adm'

/** L'ordine dei passi mostrati nell'intestazione. `letture_da_controllare` non
 * compare: in prima release e' irraggiungibile (design §6.1). */
const PASSI: readonly StatoDichiarazione[] = ['letture_non_inserite', 'bozza', 'da_firmare', 'da_inviare', 'inviata']
/** Stati per cui il corpo della schermata mostra qualcosa. Se `dichiarazione.stato`
 * ne esce (es. `letture_da_controllare`, irraggiungibile ma presente nel tipo),
 * si mostra un avviso esplicito invece di una pagina muta. */
const STATI_GESTITI: readonly StatoDichiarazione[] = [
  'letture_non_inserite',
  'bozza',
  'da_firmare',
  'da_inviare',
  'inviata',
  'errore_invio',
]
const ETICHETTA: Record<string, string> = {
  letture_non_inserite: 'Letture',
  bozza: 'Calcolo',
  da_firmare: 'Firma',
  da_inviare: 'Invio',
  inviata: 'Ricevuta',
}

export function Dichiarazione() {
  const { idImpianto, anno, periodo } = useParams()
  const impiantoId = Number(idImpianto)
  const annoImposta = Number(anno)
  const queryClient = useQueryClient()
  const [errore, setErrore] = useState<string | null>(null)
  // La generazione la fa il worker: dopo la conferma lo stato resta `bozza`
  // finche' l'XML non e' pronto, e senza interrogare la pagina non lo saprebbe.
  const [generazioneInCorso, setGenerazioneInCorso] = useState(false)

  const { data: dichiarazione, isLoading } = useQuery({
    queryKey: ['dichiarazione', impiantoId, annoImposta, periodo],
    queryFn: () => caricaDichiarazione(impiantoId, annoImposta, periodo as Periodo),
    // Si interroga solo mentre generazione o invio sono in volo, poi si ferma:
    // un invio ADM dura minuti, e un websocket non vale la sua manutenzione.
    refetchInterval: (q) => {
      const stato = q.state.data?.stato
      if (stato === 'da_inviare') return 5000
      return generazioneInCorso && stato === 'bozza' ? 2000 : false
    },
  })

  if (isLoading) return null
  if (!dichiarazione) return <p>Nessuna dichiarazione per questo periodo</p>

  async function invalida() {
    await queryClient.invalidateQueries({ queryKey: ['dichiarazione', impiantoId, annoImposta, periodo] })
  }

  return (
    <Box>
      <Stepper activeStep={Math.max(0, PASSI.indexOf(dichiarazione.stato))} sx={{ mb: 3 }}>
        {PASSI.map((p) => (
          <Step key={p}>
            <StepLabel>{ETICHETTA[p]}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {errore !== null && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {errore}
        </Alert>
      )}

      {dichiarazione.stato === 'letture_non_inserite' && (
        <Typography>Inserisci le letture dalla scheda Letture dell impianto per proseguire.</Typography>
      )}

      {dichiarazione.stato === 'bozza' && (
        <PassoBozza
          dichiarazione={dichiarazione}
          inGenerazione={generazioneInCorso}
          onConferma={async () => {
            setErrore(null)
            try {
              await confermaDichiarazione(dichiarazione.id)
              setGenerazioneInCorso(true)
              await invalida()
            } catch (e) {
              setErrore(e instanceof Error ? e.message : 'errore imprevisto')
            }
          }}
        />
      )}

      {dichiarazione.stato === 'da_firmare' && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            {FORMATI_SCARICABILI.map((f) => (
              <Button key={f} onClick={() => void scarica(dichiarazione.id, f).then(avviaDownload)}>
                Scarica {f.toUpperCase()}
              </Button>
            ))}
          </Stack>
          <Button component="label" variant="contained">
            Carica l XML firmato
            <input
              hidden
              type="file"
              aria-label="Carica l XML firmato"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setErrore(null)
                try {
                  await caricaFirmato(dichiarazione.id, file)
                  await invalida()
                } catch (err) {
                  setErrore(err instanceof Error ? err.message : 'errore imprevisto')
                }
              }}
            />
          </Button>
        </Stack>
      )}

      {dichiarazione.stato === 'da_inviare' && <Typography>Invio alle Dogane in corso</Typography>}

      {dichiarazione.stato === 'inviata' && (
        <Stack spacing={1}>
          <Typography>
            {/* «Inviata» vuol dire acquisita: l'esito dei controlli arriva dopo, per IUT. */}
            Esito delle Dogane: {statoDogane(dichiarazione.invii)}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => void scarica(dichiarazione.id, 'ricevuta').then(avviaDownload)}>
              Scarica la ricevuta
            </Button>
            <PassoRettifica dichiarazioneId={dichiarazione.id} onFatta={invalida} />
          </Stack>
        </Stack>
      )}

      {dichiarazione.stato === 'errore_invio' && (
        <Stack spacing={2}>
          <PassoErrore dichiarazione={dichiarazione} />
          <Box>
            <PassoRettifica dichiarazioneId={dichiarazione.id} onFatta={invalida} />
          </Box>
        </Stack>
      )}

      {mostraStorico(dichiarazione.stato, dichiarazione.invii) && <StoricoInvii invii={dichiarazione.invii} />}

      {!STATI_GESTITI.includes(dichiarazione.stato) && (
        <Alert severity="warning" role="alert">
          Stato non gestito: {dichiarazione.stato}
        </Alert>
      )}
    </Box>
  )
}

/** La rettifica riapre una dichiarazione gia' accettata dalle Dogane: si
 * conferma, non si fa con un click distratto. L'avviso sta dentro al dialogo
 * perche' MUI mette `aria-hidden` sul resto della pagina mentre e' aperto. */
function PassoRettifica({ dichiarazioneId, onFatta }: { dichiarazioneId: number; onFatta: () => Promise<void> }) {
  const [aperto, setAperto] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  return (
    <>
      <Button color="warning" onClick={() => setAperto(true)}>
        Rettifica
      </Button>
      <Dialog open={aperto} onClose={() => setAperto(false)}>
        <DialogTitle>Rettificare la dichiarazione?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Torna in bozza: letture e dati tornano modificabili e dovrai generare, firmare e trasmettere di nuovo.
            La ricevuta dell invio gia fatto resta consultabile.
          </DialogContentText>
          {errore !== null && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {errore}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAperto(false)}>Annulla</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={async () => {
              setErrore(null)
              try {
                await rettificaDichiarazione(dichiarazioneId)
                setAperto(false)
                await onFatta()
              } catch (e) {
                setErrore(e instanceof Error ? e.message : 'errore imprevisto')
              }
            }}
          >
            Rettifica
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/** Con un solo invio che coincide con lo stato corrente (inviata, errore,
 * invio in volo) il passo mostra gia' tutto: lo storico sarebbe un doppione.
 * Serve quando c'e' un passato, cioe' dopo una rettifica o un nuovo tentativo. */
function mostraStorico(stato: StatoDichiarazione, invii: readonly InvioPortale[]): boolean {
  if (invii.length > 1) return true
  return invii.length === 1 && !['inviata', 'errore_invio', 'da_inviare'].includes(stato)
}

function statoDogane(invii: readonly InvioPortale[]): string {
  const codice = invii[0]?.statoAdm
  return codice ? etichettaStatoAdm(codice) : 'in attesa dell elaborazione'
}

const ESITO: Record<string, string> = { ricevuta: 'Acquisita', merito: 'Respinta', trasporto: 'Non recapitata' }

function StoricoInvii({ invii }: { invii: readonly InvioPortale[] }) {
  function apri(invioId: number, formato: FormatoInvio) {
    void scaricaDaInvio(invioId, formato).then(avviaDownload)
  }
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6">Storico degli invii</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Esito</TableCell>
            <TableCell>Protocollo o errore</TableCell>
            <TableCell>File</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invii.map((i) => (
            <TableRow key={i.id}>
              <TableCell>{i.chiusoIl ? new Date(i.chiusoIl).toLocaleString('it-IT') : '—'}</TableCell>
              <TableCell>
                {i.esito ? ESITO[i.esito] : 'In corso'}
                {i.statoAdm && ` · ${etichettaStatoAdm(i.statoAdm)}`}
              </TableCell>
              <TableCell>{i.protocollo ?? i.codiceErrore ?? ''}</TableCell>
              <TableCell>
                {i.ricevutaPercorso && <Button onClick={() => apri(i.id, 'ricevuta')}>Ricevuta</Button>}
                {i.xmlFirmatoPercorso && <Button onClick={() => apri(i.id, 'xml_firmato')}>XML firmato</Button>}
                {i.esitoPercorso && <Button onClick={() => apri(i.id, 'esito')}>Esito</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

function PassoBozza({
  dichiarazione,
  inGenerazione,
  onConferma,
}: {
  dichiarazione: { id: number; completa: unknown }
  inGenerazione: boolean
  onConferma: () => Promise<void>
}) {
  const grezzo = dichiarazione.completa as { dati: Record<string, unknown>; regole: Record<string, unknown> }
  // `datiPeriodoDaJson`/`regolePeriodoDaJson` costruiscono un oggetto nuovo a ogni
  // chiamata: senza questa useMemo cambierebbero identita' a ogni render e la
  // useMemo interna di useCalcolo non memoizzerebbe mai, rieseguendo il motore
  // fiscale a ogni render.
  const [dati, regole] = useMemo(
    () => [datiPeriodoDaJson(grezzo.dati), regolePeriodoDaJson(grezzo.regole)],
    [grezzo.dati, grezzo.regole],
  )
  const esito = useCalcolo(dati, regole)

  return (
    <Stack spacing={2}>
      {esito.erroreMotore !== null && (
        <Alert severity="error" role="alert">
          {esito.erroreMotore}
        </Alert>
      )}
      {esito.diagnostiche.length > 0 && (
        <List dense>
          {esito.diagnostiche.map((d, i) => (
            <ListItem key={i}>
              <ListItemText
                primary={d.messaggio}
                secondary={[d.posizione.quadro, d.posizione.mese ? `mese ${d.posizione.mese}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      )}
      <EditorElenchi
        key={dichiarazione.id}
        dichiarazioneId={dichiarazione.id}
        elenchi={(dichiarazione.completa as { dati?: { elenchi?: Elenchi } }).dati?.elenchi ?? {}}
      />
      {esito.modello && <RiepilogoCalcolo modello={esito.modello} />}
      {/* Il valore mostrato e' indicativo: quello trasmesso lo ricalcola il worker. */}
      <Typography variant="body2" color="text.secondary">
        Anteprima indicativa: il valore trasmesso viene ricalcolato al momento della generazione.
      </Typography>
      {inGenerazione && <Typography>Generazione dell XML in corso…</Typography>}
      {/* Disabilitato durante la generazione: un secondo clic accoderebbe due volte. */}
      <Button
        variant="contained"
        disabled={inGenerazione || esito.bloccato || esito.erroreMotore !== null}
        onClick={() => void onConferma()}
      >
        Conferma e genera
      </Button>
    </Stack>
  )
}

function RiepilogoCalcolo({ modello }: { modello: ModelloDichiarazione }) {
  const r = riepilogoModello(modello)
  if (r.voci.length === 0) return null
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Riepilogo del calcolo
      </Typography>
      <Table size="small" aria-label="Riepilogo del calcolo per quadro">
        <TableHead>
          <TableRow>
            <TableCell>Quadro</TableCell>
            <TableCell>Contenuto</TableCell>
            <TableCell align="right">Righe</TableCell>
            <TableCell align="right">Energia</TableCell>
            <TableCell align="right">Accisa</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {r.voci.map((v) => (
            <TableRow key={v.quadro}>
              <TableCell>{v.quadro}</TableCell>
              <TableCell>{v.etichetta}</TableCell>
              <TableCell align="right">{v.righe}</TableCell>
              <TableCell align="right">{v.kwh.isZero() ? '—' : formattaKwh(v.kwh)}</TableCell>
              <TableCell align="right">{v.accisa.isZero() ? '—' : formattaEuro(v.accisa)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
              Accisa dovuta nel periodo
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              {formattaEuro(r.accisa)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  )
}

function PassoErrore({
  dichiarazione,
}: {
  dichiarazione: { erroreCodice: string | null; erroreDescrizione: string | null }
}) {
  const s = spiegaErroreAdm(dichiarazione.erroreCodice ?? '', dichiarazione.erroreDescrizione ?? '')
  return (
    <Stack spacing={1}>
      <Typography variant="h6">{s.titolo}</Typography>
      {s.comeRisolvere !== '' && <Typography>{s.comeRisolvere}</Typography>}
      <Typography variant="body2" color="text.secondary">
        {s.testoOriginale}
      </Typography>
    </Stack>
  )
}
